import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../../actions';
import { showConfirmationModal } from '../../../actions/App';
import { fetchCustomFields } from '../../../actions/Metadata';

import { APIdeleteCustomField } from '../../../api/Metadata';

import {
  customFieldTarget,
  customFieldSource,
} from '../../../helpers/DNDCustomField';
import ItemTypes from '../../../helpers/DNDItemTypes';
import { getCurrentURLParams } from '../../../helpers/URLParams';

import EmptyTableCell from '../../EmptyTableCell';
import { getCustomFieldTypeLabel } from '../../../helpers/Metadata';
import { getBackButtonMeta } from '../../../helpers/App';

const AdminCustomFieldsListItem = ({
  intl,
  customField,
  handleDeleteCustomField,
  deleteCustomFieldFromList,
  isDragging,
  connectDragSource,
  connectDropTarget,
}) => {
  const location = useLocation();

  const linkTo = {
    pathname: `/admin/settings/custom-fields/${customField.id}/edit`,
    state: {
      goBackToListButton: {
        ...getBackButtonMeta('custom-fields'),
        search: getCurrentURLParams(location),
      },
    },
  };

  return connectDragSource(connectDropTarget((
    <tr className={classnames({ dragging: isDragging })}>
      <th scope="row" className="wrapper-drag-and-drop-icon">
        <i className="fa-solid fa-grip-vertical" />
      </th>
      <th scope="row" className="custom-field-name">
        <span className="custom-field-name-content">
          <Link to={linkTo}>
            {customField.name}
          </Link>
        </span>
      </th>
      <td className="custom-field-type">
        {getCustomFieldTypeLabel(customField)}
      </td>
      <td className="custom-field-visible-on-shares">
        {customField.show_on_shares ? (
          <i className="far fa-check" />
        ) : (
          <EmptyTableCell />
        )}
      </td>
      <td className="custom-field-required">
        {customField.required ? (
          <i className="far fa-check" />
        ) : (
          <EmptyTableCell />
        )}
      </td>
      <td className="action-more">
        <UncontrolledDropdown>
          <DropdownToggle nav className="options">
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right>
            <Link to={linkTo} className="dropdown-item-link">
              <DropdownItem tag="span">
                <i className="fas fa-pencil" />
                {intl.formatMessage({ defaultMessage: 'Edit' })}
              </DropdownItem>
            </Link>
            <DropdownItem divider />
            <DropdownItem
              onClick={(e) => {
                e.preventDefault();
                handleDeleteCustomField(customField, deleteCustomFieldFromList);
              }}
            >
              <i className="fas fa-trash" />
              {intl.formatMessage({ defaultMessage: 'Delete' })}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </td>
    </tr>
  )));
};

const mapDispatchToProps = (dispatch, props) => ({
  openTaxonomyDeleteModal: (params) => {
    dispatch(showModal('TaxonomyDeleteModal', params));
  },
  handleDeleteCustomField: (customField, callback) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: `Are you sure you want to delete this Custom Field?`,
      }),
      onConfirm: () => (
        APIdeleteCustomField(customField.id).then(() => {
          dispatch(fetchCustomFields());
          callback(customField);
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
});

AdminCustomFieldsListItem.propTypes = {
  intl: PropTypes.object.isRequired,
  customField: PropTypes.object.isRequired,
  handleDeleteCustomField: PropTypes.func.isRequired,
  deleteCustomFieldFromList: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
};

const enhance = compose(
  DragSource(ItemTypes.CUSTOM_FIELD, customFieldSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    isDragging: monitor.isDragging(),
  })),
  DropTarget(ItemTypes.CUSTOM_FIELD, customFieldTarget, DNDconnect => ({
    connectDropTarget: DNDconnect.dropTarget(),
  })),
  connect(null, mapDispatchToProps),
);

export default injectIntl(enhance(AdminCustomFieldsListItem));
